import React from 'react'
import {
  SimplePaletteColorOptions,
  Theme,
  ThemeOptions,
  createTheme as createMuiTheme
} from '@mui/material/styles'
import { deepObjectMerge } from 'widgets-shared/utilities'
import { deepObjectCopy } from 'widgets-shared/utilities'

import { lightGreen } from '@mui/material/colors'

import { GlobalConfig } from 'smartstream-widgets/types/sharedModule/core'

interface NeutralColor {
  0: string
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string
  600: string
  700: string
  800: string
  900: string
  950: string
}

interface CustomColor {
  homeTeam?: string
  awayTeam?: string
}

interface AlphaColor {
  A0: string
  A100: string
  A200: string
  A300: string
  A400: string
  A500: string
  BrightA: string
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    widget: string,
    surface1: string,
    surface2: string,
    surface3: string,
    surface4: string
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    cs: true,
    c420: true,
    c576: true,
    c728: true
  }

  interface PaletteOptions {
    neutral?: NeutralColor
    alpha?: AlphaColor
    custom?: CustomColor
  }

  interface ThemeOptions {
    neutral?: NeutralColor
    alpha?: AlphaColor
    custom?: CustomColor
  }

  interface Theme {
    neutral?: NeutralColor
    alpha?: AlphaColor
    custom?: CustomColor
  }
  interface Palette {
    neutral?: NeutralColor
    alpha?: AlphaColor
    custom?: CustomColor
  }
}

declare module '@mui/material/styles/createTypography' {
  interface Typography {
    variantFontFamily: React.CSSProperties['fontFamily']
  }
}

declare module '@mui/material/styles/createTheme' {
  interface Theme {
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
  }
}

export type GlobalConfigWithTheme = GlobalConfig & {
  partialTheme: ThemeOptions
}
type SelectButtonTheme = ((theme: Theme, config: GlobalConfigWithTheme, themeType: string) => Theme)

export const GeniusThemeBase: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      c420: 420,
      cs: 480,
      c576: 576,
      sm: 600,
      c728: 728,
      md: 960,
      lg: 1280,
      xl: 1920,
    }
  },
  palette: {
    background: {
      paper: "#FFFFFF",
      default: "#FAFAFA",
      surface1: "#1212120A",
      surface2: "#12121214",
      surface3: "#12121229",
      surface4: "#12121252"
    },
    mode: "light",
    primary: {
      main: "#0000DC"
    },
    secondary: {
      main: "#f76a6a "
    },
    text: {
      primary: "#151B34"
    },
    error: {
      main: "#F44336"
    },
    warning: {
      main: "#ff9800"
    },
    success: {
      main: "#18C971"
    },
    info: {
      main: "#2196f3"
    },
    grey: {
      50: "#F2F2F5",
      100: "#E5E5EA",
      200: "#C7C7CC",
      300: "#AEAEB2",
      400: "#8E8E93",
      500: "#636366",
      600: "#48484A",
      700: "#3A3A3C",
      800: "#2C2C2E",
      900: "#1C1C1E"
    },
    neutral: {
      0: '#000000',
      50: '#1C1C1E',
      100: '#2C2C2E',
      200: '#3A3A3C',
      300: '#48484A',
      400: '#636366',
      500: '#8E8E93',
      600: '#AEAEB2',
      700: '#C7C7CC',
      800: '#E5E5EA',
      900: '#F2F2F5',
      950: '#FFFFFF',
    },
    custom : {
      homeTeam: '#6383D6',
      awayTeam: '#F93939'
    }
  },
  typography: {
    fontFamily: ['Red-hat', 'Lato', 'Arial', 'sans-serif'].join(','),
    button: {
      textTransform: 'unset'
    }
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      }
    }
  }
}

export const GeniusThemeBaseDark: ThemeOptions = deepObjectMerge(
  {},
  deepObjectCopy(GeniusThemeBase),
  {
    palette: {
      background: {
        paper: "#FFFFFF14",
        default: "#151B34",
        surface1: "#FFFFFF0A",
        surface2: "#FFFFFF14",
        surface3: "#FFFFFF29",
        surface4: "#FFFFFF52"
      },
      mode: "dark",
      primary: {
        main: "#E1FF67"
      },
      secondary: {
        main: "#95ECFD"
      },
      text: {
        primary: "#FFFFFF"
      },

    }
  }
)

export const themeTypes = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  DEFAULT: 'default',
  ERROR: 'error',
  SUCCESS: 'success'
}

export const createSubTheme = (config: GlobalConfigWithTheme, color: string) => {
  let newPartialTheme: ThemeOptions
  if (config.partialTheme) {
    newPartialTheme = deepObjectCopy(config.partialTheme) as ThemeOptions
  } else {
    newPartialTheme = getBaseTheme()
  }

  if (!newPartialTheme.palette) {
    newPartialTheme.palette = {
      primary: {
        main: color
      }
    }
  } else if (!newPartialTheme.palette.primary) {
    newPartialTheme.palette.primary = {
      main: color
    }
  } else {
    (newPartialTheme.palette.primary as SimplePaletteColorOptions).main = color
  }
  return createTheme(newPartialTheme)
}

export const selectTheme: SelectButtonTheme = (theme, config, themeType): Theme => {
  switch (themeType) {
    case themeTypes.PRIMARY:
      return createSubTheme(config, theme.palette.primary.main)
    case themeTypes.SECONDARY:
      return createSubTheme(config, theme.palette.secondary.main)
    case themeTypes.SUCCESS:
      return createSubTheme(config, lightGreen[800])
    case themeTypes.ERROR:
      return createSubTheme(config, theme.palette.error.main)
    default:
      return createSubTheme(config, theme.palette.grey[300])
  }
}

export function createThemeObject(partialTheme: ThemeOptions, geniusThemeBase: ThemeOptions = GeniusThemeBase) {
  return deepObjectMerge({}, deepObjectCopy(geniusThemeBase), partialTheme)
}

export function createTheme(partialTheme: ThemeOptions, darkMode: boolean = false) {
  return createMuiTheme(createThemeObject(
    partialTheme,
    partialTheme?.palette?.mode === 'dark' || darkMode
      ? GeniusThemeBaseDark
      : GeniusThemeBase
  ))
}

export function getBaseTheme(): ThemeOptions {
  return deepObjectCopy(GeniusThemeBase) as ThemeOptions
}
